import type { AnchorHTMLAttributes, ButtonHTMLAttributes, JSX, ReactNode } from 'react';
import { cn } from '@lib/cn';

/**
 * Visual variant of the Button.
 *
 * - `primary`   — main CTA (e.g. "Falar no WhatsApp"); brand-filled.
 * - `secondary` — outline CTA (e.g. "Ver detalhes"); neutral border.
 * - `ghost`     — text-only CTA (e.g. "Ver como funciona"); no border / fill.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

/**
 * Size token of the Button (controls height + horizontal padding).
 *
 * - `md` (44px) for in-flow / secondary CTAs.
 * - `lg` (56px) for the Hero primary CTA — Apple HIG / WCAG 2.5.5 AAA target.
 */
export type ButtonSize = 'md' | 'lg';

type BaseButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  className?: string;
  children?: ReactNode;
};

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  keyof BaseButtonProps | 'children'
>;

type NativeAnchorProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  keyof BaseButtonProps | 'children' | 'href'
>;

type ButtonAsButton = BaseButtonProps & { as?: 'button' } & NativeButtonProps;

type ButtonAsAnchor = BaseButtonProps & {
  as: 'a';
  href: string;
} & NativeAnchorProps;

/**
 * Props of the polymorphic Button.
 *
 * Discriminated by the literal `as` prop:
 * - `as` omitted or `'button'` -> renders `<button type="button">` and accepts
 *   every native `HTMLButtonElement` attribute (onClick, disabled, type, ...).
 * - `as: 'a'` -> renders `<a>` and REQUIRES `href`. Accepts every native
 *   `HTMLAnchorElement` attribute (target, rel, download, ...).
 *
 * Security note: when rendering as an external link with `target="_blank"`,
 * the caller MUST also pass `rel="noopener noreferrer"` to mitigate reverse
 * tabnabbing (T-04-01). The Button does NOT inject `rel` automatically.
 *
 * Accessibility note: when the Button has no visible label (icon-only) or an
 * ambiguous label, the caller MUST provide `aria-label` (T-04-02).
 */
export type ButtonProps = ButtonAsButton | ButtonAsAnchor;

const BASE_CLASSES =
  'inline-flex items-center justify-center rounded-xl font-semibold text-base ' +
  'transition-colors duration-150 active:translate-y-px ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-50 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-brand-500 text-white shadow-md hover:bg-brand-600 hover:shadow-lg',
  secondary:
    'bg-transparent border border-neutral-300 text-neutral-800 hover:bg-neutral-100 hover:border-neutral-400',
  ghost: 'bg-transparent text-neutral-800 hover:text-brand-600 hover:underline underline-offset-4',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  md: 'h-11 px-5 gap-2',
  lg: 'h-14 px-6 gap-2',
};

/**
 * Polymorphic Button primitive.
 *
 * @example Primary CTA as anchor (Hero WhatsApp)
 * ```tsx
 * <Button
 *   variant="primary"
 *   size="lg"
 *   as="a"
 *   href={whatsappLink(message, 'hero-cta')}
 *   target="_blank"
 *   rel="noopener noreferrer"
 *   icon={<WhatsAppIcon className="h-5 w-5" />}
 *   aria-label="Falar no WhatsApp com a Omanna — abre em nova aba"
 * >
 *   Falar no WhatsApp
 * </Button>
 * ```
 */
export const Button = (props: ButtonProps): JSX.Element => {
  const {
    variant = 'primary',
    size = 'md',
    icon,
    iconPosition = 'left',
    className,
    children,
    as,
    ...rest
  } = props;

  const classes = cn(BASE_CLASSES, VARIANT_CLASSES[variant], SIZE_CLASSES[size], className);

  const content = (
    <>
      {icon !== undefined && iconPosition === 'left' ? icon : null}
      {children}
      {icon !== undefined && iconPosition === 'right' ? icon : null}
    </>
  );

  if (as === 'a') {
    const anchorRest = rest as NativeAnchorProps & { href: string };
    return (
      <a className={classes} {...anchorRest}>
        {content}
      </a>
    );
  }

  const { type = 'button', ...buttonRest } = rest as NativeButtonProps;
  return (
    <button type={type} className={classes} {...buttonRest}>
      {content}
    </button>
  );
};
